import { useState, useEffect } from 'react';
import { useUserRole, UserRole } from '@/hooks/useUserRole';

const DEV_MODE_STORAGE_KEY = 'dev-mode-simulated-role';

export const useDevMode = () => {
  const { role: actualRole, loading } = useUserRole();
  const [simulatedRole, setSimulatedRoleState] = useState<UserRole | null>(null);

  // Load simulated role from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(DEV_MODE_STORAGE_KEY);
    if (stored && ['user', 'moderator', 'admin', 'system_admin'].includes(stored)) {
      setSimulatedRoleState(stored as UserRole);
    }
  }, []);

  // Only system_admin can use dev mode
  const canUseDevMode = actualRole === 'system_admin';

  // Wrapper to also persist to localStorage
  const setSimulatedRole = (role: UserRole | null) => {
    setSimulatedRoleState(role);
    if (role) {
      localStorage.setItem(DEV_MODE_STORAGE_KEY, role);
    } else {
      localStorage.removeItem(DEV_MODE_STORAGE_KEY);
    }
  };

  // Clear simulation if user is not system_admin
  useEffect(() => {
    if (!loading && actualRole !== 'system_admin' && simulatedRole) {
      setSimulatedRole(null);
    }
  }, [actualRole, loading, simulatedRole]);

  // Return simulated role if active, otherwise actual role
  const effectiveRole = canUseDevMode && simulatedRole ? simulatedRole : actualRole;

  return {
    actualRole,
    effectiveRole,
    simulatedRole,
    setSimulatedRole,
    canUseDevMode,
    isSimulating: canUseDevMode && simulatedRole !== null,
    loading
  };
};
